'use client';

import Heading from '~/components/atoms/Heading';
import Media from '~/components/molecules/Media';
import WysiwygString from '~/components/molecules/WysiwygString';
import { MediaSchema } from '~/lib/api/fragments/media';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

interface PropertiesPageHeaderProps {
  title: string;
  description?: string | null;
  media?: MediaSchema['media'];
}

const PropertiesPageHeader: React.FC<PropertiesPageHeaderProps> = ({
  title,
  description,
  media,
}) => {
  const isDesktop = useHydrationSafeResponsive('desktop');

  return (
    <header className="relative isolate flex grid-cols-[4fr_6fr] items-center justify-center lg:h-[calc(100svh-140px)]">
      <aside className="bg-brand-secondary relative z-10 w-full px-4 pt-20 pb-10 text-white lg:w-auto lg:p-24">
        <div className="flex flex-col items-center gap-y-6 lg:min-w-110 lg:gap-y-20">
          <div className="row-start-2 flex flex-col gap-y-2 self-start lg:items-center lg:self-auto lg:text-center">
            {description && (
              <div className="text-2xs text-brand-quinary relative">
                <WysiwygString html={description} />
              </div>
            )}

            <Heading as="h1" level={3}>
              <WysiwygString html={title} />
            </Heading>
          </div>

          {!isDesktop && (
            <div className="relative w-full">
              {media && (
                <Media
                  media={media}
                  imageProps={{
                    className: 'w-full h-[280px] object-cover',
                    width: 360,
                    height: 280,
                    priority: true,
                  }}
                  videoProps={{
                    className:
                      'w-full h-full max-h-[280px] lg:max-h-auto object-cover',
                    autoPlay: true,
                    playsInline: true,
                    loop: true,
                    muted: true,
                  }}
                  reactPlayerProps={{
                    className: '!w-full !h-full !object-cover',
                    playing: true,
                    playsInline: true,
                    loop: true,
                    muted: true,
                  }}
                />
              )}
            </div>
          )}
        </div>
      </aside>

      {isDesktop && (
        <div className="absolute top-0 left-0 flex h-full w-full bg-black">
          {media && (
            <Media
              media={media}
              imageProps={{
                className: 'w-full h-full object-cover',
                fill: true,
              }}
              videoProps={{
                className: 'w-full h-full object-cover',
                autoPlay: true,
                playsInline: true,
                loop: true,
                muted: true,
              }}
              reactPlayerProps={{
                className: '!w-full !h-full !object-cover',
                playing: true,
                playsInline: true,
                loop: true,
                muted: true,
              }}
            />
          )}
        </div>
      )}
    </header>
  );
};

export default PropertiesPageHeader;
